import { useEffect, useState } from 'react'
import { ArrowLeft, ArrowRight, CheckCircle2 } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { EmptyState } from '@/components/EmptyState'
import { useToast } from '@/hooks/useToast'
import type { TaxonomyItem } from '@/features/coa/types'
import {
  ClassificationGroupCard,
  type ClassificationGroup,
  type CoaSelection,
} from './ClassificationGroupCard'

interface Props {
  groups: ClassificationGroup[]
  selections: Record<string, CoaSelection | null>
  taxonomy: TaxonomyItem[]
  onSelect: (groupKey: string, sel: CoaSelection) => void
  onClear: (groupKey: string) => void
  onCreateRecord: (draft: {
    statement_type: string
    main_category: string
    subcategory: string
    account_code: string
  }) => Promise<void>
  onSave: () => void
  onCancel: () => void
  saveLabel?: string
  title?: string
  description?: string
}

/**
 * Shared "classify transactions" stepper used by both the upload wizard
 * and the Plaid classification flow. Shows one group at a time with
 * Back/Next/Cancel + "Save & Finish" on the last group.
 */
export function ClassifyStepper({
  groups,
  selections,
  taxonomy,
  onSelect,
  onClear,
  onCreateRecord,
  onSave,
  onCancel,
  saveLabel = 'Save COA & Finish',
  title = 'Map Chart of Accounts',
  description,
}: Props) {
  const { toast } = useToast()
  const [idx, setIdx] = useState(0)

  useEffect(() => {
    if (idx > groups.length - 1) setIdx(Math.max(0, groups.length - 1))
  }, [groups.length, idx])

  const totalRows = groups.reduce((n, g) => n + 1 + g.children.length, 0)
  const decided = groups.filter((g) => !!selections[g.key]).length
  const currentGroup = groups[idx]
  const isFirst = idx === 0
  const isLast = idx === groups.length - 1

  const goNext = () => {
    if (!currentGroup) return
    if (!selections[currentGroup.key]) {
      toast({
        variant: 'destructive',
        title: 'Pick a Chart of Account',
        description: 'Select an AI suggestion or pick manually before continuing.',
      })
      return
    }
    if (!isLast) setIdx(idx + 1)
  }
  const goBack = () => {
    if (!isFirst) setIdx(idx - 1)
  }

  if (!currentGroup) {
    return (
      <Card className="p-6">
        <EmptyState title="No transactions to classify" description="Please restart." />
      </Card>
    )
  }

  const desc =
    description ??
    `${groups.length} unique transaction type${groups.length === 1 ? '' : 's'} (${totalRows} row${totalRows === 1 ? '' : 's'} total). Pick a Chart of Account for each group - your choice applies to all similar transactions in that group.`

  return (
    <div className="space-y-4">
      <Card className="p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-base font-semibold sm:text-lg">{title}</h3>
            <p className="text-sm text-muted-foreground">{desc}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">
              Group {idx + 1} of {groups.length}
            </Badge>
            <Badge variant={decided === groups.length ? 'success' : 'warning'}>
              {decided}/{groups.length} decided
            </Badge>
          </div>
        </div>
        <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full bg-primary transition-all"
            style={{ width: `${((idx + 1) / groups.length) * 100}%` }}
          />
        </div>
      </Card>

      <ClassificationGroupCard
        key={currentGroup.key}
        group={currentGroup}
        selection={selections[currentGroup.key] ?? null}
        taxonomy={taxonomy}
        onSelect={(sel) => onSelect(currentGroup.key, sel)}
        onClear={() => onClear(currentGroup.key)}
        onCreateRecord={onCreateRecord}
      />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={goBack} disabled={isFirst}>
            <ArrowLeft className="size-4" /> Back
          </Button>
          {isLast ? (
            <Button type="button" variant="primary" onClick={onSave}>
              <CheckCircle2 className="size-4" /> {saveLabel}
            </Button>
          ) : (
            <Button type="button" variant="primary" onClick={goNext}>
              Next <ArrowRight className="size-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
